'use client';

// Third Party
import * as React from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import * as Sentry from '@sentry/nextjs';

// Utils
import { routes } from '@routes';
import { useQuery, useMutation } from '@apollo/client/react';
import { UserReferralCampaignsForCurrentUserDocument, UserReferralCampaignCreateDocument, UserReferralCampaignState, ReferralCampaignType, type UserReferralCampaignsForCurrentUserQuery } from '@gql';
import getConfig from '@config';
import { useAnalytics } from '@contexts/use-analytics';
import { useNotification } from '@contexts/use-notification';

// UI
import { Typography } from '@components/typography';
import { Button } from '@components/button';
import { ReferralCodeCard } from '@components/referral-code-card';

// Components
import { HowItWorks } from './how-it-works';
import { RewardsInfo } from './rewards-info';
import { FAQSection } from './faq-section';
interface ReferAndEarnProps {
  isAuthenticated: boolean;
  initialCampaignData: UserReferralCampaignsForCurrentUserQuery | null;
}
export const ReferAndEarn: React.FC<ReferAndEarnProps> = ({
  isAuthenticated,
  initialCampaignData
}) => {
  const router = useRouter();
  const {
    addNotification
  } = useNotification();
  const {
    emitReferralJoinedProgramEvent
  } = useAnalytics();

  // Track if we've mutated and need fresh data
  const [needsRefetch, setNeedsRefetch] = React.useState(false);

  // Query user's referral campaigns - only runs when we need to refetch after mutation
  const {
    data: refetchedData,
    refetch
  } = useQuery(UserReferralCampaignsForCurrentUserDocument, {
    variables: {
      includeInactive: false
    },
    skip: !needsRefetch,
    fetchPolicy: 'network-only'
  });

  // Use refetched data if available, otherwise use initial server data
  const campaignsData = refetchedData || initialCampaignData;

  // Check if query returned auth error (backend issue)
  const queryAuthError = campaignsData?.userReferralCampaignsForCurrentUser?.__typename === 'UnauthenticatedError';

  // Create campaign mutation
  const [createCampaign, {
    loading: creatingCampaign
  }] = useMutation(UserReferralCampaignCreateDocument);

  // Check if user has an active referral campaign
  const activeCampaign = React.useMemo(() => {
    if (campaignsData?.userReferralCampaignsForCurrentUser?.__typename === 'UserReferralCampaignCollection') {
      const campaigns = campaignsData.userReferralCampaignsForCurrentUser.entries;
      return campaigns.find((c: {
        state: UserReferralCampaignState;
      }) => c.state === UserReferralCampaignState.Active);
    }
    return null;
  }, [campaignsData]);
  const isRegistered = !!activeCampaign;
  const handleCreateReferralLink = async () => {
    if (!isAuthenticated) {
      // Redirect to signup with callback to return here
      router.push(routes.signup({
        redirect: routes.referAndEarn
      }), {
        scroll: false
      });
      return;
    }
    try {
      const result = await createCampaign({
        variables: {
          input: {
            campaignType: ReferralCampaignType.ReferredUserBonus
          }
        }
      });
      const response = result.data?.userReferralCampaignCreate;
      if (response?.__typename === 'UserReferralCampaign') {
        // Success - enable refetch and trigger it
        emitReferralJoinedProgramEvent();
        addNotification({
          title: 'Referral link created!',
          message: 'You can now share your referral link with friends.',
          type: 'success'
        });
        setNeedsRefetch(true);
        refetch();
      } else if (response?.__typename === 'UnauthenticatedError') {
        // Session expired or invalid - redirect to login
        addNotification({
          title: 'Session expired',
          message: 'Please log in again to create your referral link.',
          type: 'error'
        });
        window.location.href = '/login';
      } else if (response?.__typename === 'ValidationErrorCollection') {
        // Validation error (e.g., already registered)
        const errorMessage = response.errors?.[0]?.message || 'Unable to create referral link.';
        addNotification({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        // Refetch in case they're already registered
        setNeedsRefetch(true);
        refetch();
      }
    } catch (error) {
      Sentry.captureException(error);
      addNotification({
        title: 'Error',
        message: 'Something went wrong. Please try again.',
        type: 'error'
      });
    }
  };
  const {
    publicRuntimeConfig: {
      canonicalUrl
    }
  } = getConfig();
  const referralUrl = activeCampaign ? `${canonicalUrl}signup?ref=${activeCampaign.code}` : '';
  return <div className="text-primary-main">
      {/* Hero Section */}
      <div className="bg-off-white py-10 md:py-12 relative overflow-hidden">
        <div className="body-container text-center relative z-10">
          {/* Icon */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-6">
            <div className="flex items-center justify-center">
              <span className="text-5xl" role="img" aria-label="money">
                💸
              </span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }}>
            <Typography variant="title1" component="h1" className="text-primary-main mb-4 text-4xl md:text-5xl lg:text-6xl">
              Give $50, Get $50
            </Typography>
          </motion.div>

          {/* Subheadline */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <Typography variant="body1" component="p" className="text-primary-main/80 max-w-xl mx-auto mb-8 text-lg leading-relaxed">
              Share Savvy with friends and you'll both earn $50 cash back on
              your next stay. Refer up to 5!
            </Typography>
          </motion.div>

          {/* CTA Button or Referral Code Display */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }}>
            {!isRegistered && !queryAuthError && <Button color="default" variant="filled" size="lg" className="rounded-full px-10 py-4 text-lg font-medium shadow-lg hover:shadow-xl transition-shadow" onClick={handleCreateReferralLink} isLoading={creatingCampaign}>
                {isAuthenticated ? 'Get Your Referral Code' : 'Start Referring Friends'}
              </Button>}
            {isRegistered && activeCampaign && <ReferralCodeCard code={activeCampaign.code} referralUrl={referralUrl} />}
            {queryAuthError && isAuthenticated && <Typography variant="body2" component="p" className="text-primary-main/60">
                Referral program is temporarily unavailable. Please try again
                later.
              </Typography>}
          </motion.div>

          {/* Social Proof */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mt-8 flex items-center justify-center gap-2 text-sm">
            <span>Join thousands of members already earning rewards</span>
          </motion.div>
        </div>
      </div>

      {/* How It Works Section */}
      <HowItWorks />

      {/* What You Get / What Your Friend Gets */}
      <RewardsInfo />

      {/* FAQs Section */}
      <FAQSection />
    </div>;
};